// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import { useQuery } from '@tanstack/react-query';
import { getAllUsers } from '../services/userService';
import { useSharedSession } from '../auth/useSharedSession';
import { deriveEffectiveRole } from '../../helios/services/authService';
import type { UserRole } from '../../helios/types';
import type { UserWithOrg } from '../types/user';
import { ROLE_LABELS } from '../roles/roleLabels';

export interface UserStats {
  activeAccounts: number;
  pendingInvites: number;
  elevatedAccess: number;
}

export interface RoleBreakdown {
  role: string;
  label: string;
  count: number;
  pct: number;
}

export interface UsersView {
  users: UserWithOrg[];
  stats: UserStats;
  roleBreakdown: RoleBreakdown[];
  isLoading: boolean;
  error: string | null;
}

function deriveStats(users: UserWithOrg[]): UserStats {
  return {
    activeAccounts: users.filter(u => u.status === 'active').length,
    pendingInvites: users.filter(u => u.status !== 'active').length,
    elevatedAccess: users.filter(u => deriveEffectiveRole(u) === 'admin').length,
  };
}

function deriveRoleBreakdown(users: UserWithOrg[]): RoleBreakdown[] {
  const counts = new Map<UserRole, number>();
  for (const u of users) {
    // One bucket per user, by the same rank-based pick the session uses; users
    // with no recognized IAM role are left out of the breakdown.
    const role = deriveEffectiveRole(u);
    if (role) counts.set(role, (counts.get(role) ?? 0) + 1);
  }
  const total = users.length;
  return Array.from(counts.entries()).map(([role, count]) => ({
    role,
    label: ROLE_LABELS[role],
    count,
    pct: total > 0 ? Math.round((count / total) * 100) : 0,
  }));
}

/**
 * Org-scoped user list and derived stats for Admin Center's Home dashboard,
 * via `/api/v1/users` (`/api/v1/admin/all-users` is deprecated). `UserWithOrg`
 * now carries site access (`all_sites`/`sites`), but no "no site access" stat
 * is derived from it yet.
 */
export function useUsers(): UsersView {
  const { organizationId, isLoading: sessionLoading } = useSharedSession();

  const query = useQuery({
    queryKey: ['admin-center', 'users', organizationId],
    queryFn: () => getAllUsers({ tenantId: organizationId ?? undefined }),
    enabled: !sessionLoading,
  });

  const users = query.data ?? [];

  return {
    users,
    stats: deriveStats(users),
    roleBreakdown: deriveRoleBreakdown(users),
    isLoading: query.isPending,
    error: query.isError ? 'Unable to load users.' : null,
  };
}
